import React, { useEffect, useState } from 'react';
import { trackContentLinkClick } from './track';
import { getLocalizedHref } from '../../utils/get-localized-url';
import type { ContentLinkAnalytics } from './track';
import { useContentLinkViewedAnalytics } from '../../hooks/use-content-link-viewed-analytics';

interface Props {
    href: string;
    text?: string;
    className?: string;
    setRef?: React.Ref<any>;
    children?: React.ReactNode;
    // when the link is clicked the link text will be sent as the anaylics payload, dataName is sent instead if passed in
    dataName?: string;
    target?: string;
    onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
    onAuxClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
    onContextMenu?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
    onMouseEnter?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
    onMouseLeave?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
    /** Optional content link analytics metadata */
    contentLinkAnalytics?: ContentLinkAnalytics;
}

const ATLASSIAN_DOMAINS = ['atlassian.com'];

/**
 * Determines the appropriate rel attribute for a given URL.
 * We only want 'noreferrer noopener' to be used for external links.
 */
const determineRelAttribute = (href: string): string | null => {
    // The constructor will only update the hostname to that of the `base` parameter if the hostname doesn't already exist.
    const url = new URL(href, 'https://relative');

    const firstParty =
        url.hostname === 'relative' ||
        ATLASSIAN_DOMAINS.some(
            (d) => url.hostname === d || url.hostname.endsWith(`.${d}`)
        );
    return firstParty ? null : 'noreferrer noopener';
};

/**
 * Creates a link to a URL.
 * @example
 * <AtlasLink href="https://www.atlassian.com">Click me!</AtlasLink>
 */
const AtlasLink = ({
    href,
    text,
    className,
    children,
    dataName,
    setRef,
    contentLinkAnalytics,
    onClick: customOnClick,
    ...rest
}: Props) => {
    const [localizedHref, setLocalizedHref] = useState<string>(href);
    const rel = determineRelAttribute(href) ?? undefined;

    useEffect(() => {
        setLocalizedHref(getLocalizedHref(href));
    }, [href]);

    // Automatically track viewed event when contentLinkAnalytics is provided.
    // AtlasLink now handles both the "viewed" (render) and "clicked" (interaction) analytics events
    // without requiring users to call useContentLinkViewedAnalytics separately.
    useContentLinkViewedAnalytics(
        contentLinkAnalytics
            ? contentLinkAnalytics
            : {
                  shouldSkip: true
              }
    );

    return (
        <a
            ref={setRef}
            href={localizedHref}
            data-testid="atlas_link"
            className={className}
            rel={rel}
            {...rest}
            onClick={(e) => {
                // Call custom onClick first if provided (e.g., component-specific analytics)
                customOnClick?.(e);

                // Then call AtlasLink's content-link tracking
                // (only if the custom handler didn't prevent default or stop propagation)
                if (!e.defaultPrevented) {
                    trackContentLinkClick({
                        href,
                        text,
                        name: dataName,
                        contentLinkAnalytics
                    });
                }
            }}
        >
            {children ? children : text}
        </a>
    );
};
export default AtlasLink;
